import { useEffect, useMemo, useState } from 'react';
import { categories, comics, type Comic } from '@/mocks/comics';
import ComicCard from './ComicCard';
import ComicModal from './ComicModal';

export default function ComicGrid() {
  const [active, setActive] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [followed, setFollowed] = useState<number[]>([]);
  const [selected, setSelected] = useState<Comic | null>(null);
  const [toast, setToast] = useState('');

  // listen for filter/search events dispatched by nav/categories
  useEffect(() => {
    const section = document.querySelector<HTMLElement>('#comics');
    if (!section) return;
    const onFilter = (e: Event) => {
      const key = (e as CustomEvent).detail as string;
      if (categories.some((c) => c.key === key)) setActive(key);
      setKeyword('');
    };
    const onSearch = (e: Event) => {
      setActive('all');
      setKeyword(((e as CustomEvent).detail as string) || '');
    };
    section.addEventListener('comic:filter', onFilter);
    section.addEventListener('comic:search', onSearch);
    return () => {
      section.removeEventListener('comic:filter', onFilter);
      section.removeEventListener('comic:search', onSearch);
    };
  }, []);

  const list = useMemo(() => {
    let base = active === 'all' ? comics : comics.filter((c) => c.category === active);
    const k = keyword.trim().toLowerCase();
    if (k) {
      base = base.filter(
        (c) =>
          c.title.toLowerCase().includes(k) ||
          c.author.toLowerCase().includes(k) ||
          c.tags.some((t) => t.toLowerCase().includes(k))
      );
    }
    return base;
  }, [active, keyword]);

  const toggleFollow = (id: number) => {
    setFollowed((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setToast(followed.includes(id) ? '已取消追更' : '已加入追更，更新第一时间提醒你');
    window.setTimeout(() => setToast(''), 2200);
  };

  const openComic = (c: Comic) => setSelected(c);

  return (
    <section id="comics" className="w-full bg-background-100 py-14 md:py-20" aria-labelledby="comics-title">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
              <i className="ri-emotion-laugh-line" aria-hidden="true"></i>
              爆笑连载
            </span>
            <h2 id="comics-title" className="mt-3 font-heading text-2xl text-foreground-950 md:text-4xl">
              今日爆笑连载
            </h2>
            <p className="mt-3 max-w-xl text-sm text-foreground-600 md:text-base">
              切换分类或使用上方搜索，就能快速找到戳中你笑点的作品，点开封面立即开刷。
            </p>
          </div>
          {keyword && (
            <span className="flex items-center gap-2 text-sm text-foreground-600">
              搜索“{keyword}”，共 {list.length} 部
              <button
                type="button"
                onClick={() => setKeyword('')}
                className="flex h-6 w-6 items-center justify-center rounded-full bg-background-300 text-foreground-700 hover:bg-primary-200"
                aria-label="清除搜索"
              >
                <i className="ri-close-line text-sm" aria-hidden="true"></i>
              </button>
            </span>
          )}
        </div>

        {/* category tabs */}
        <div className="mt-7 flex flex-wrap gap-2 md:gap-3" role="tablist" aria-label="漫画分类筛选">
          {categories.map((c) => (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={active === c.key}
              onClick={() => {
                setActive(c.key);
                setKeyword('');
              }}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                active === c.key
                  ? 'bg-primary-500 font-medium text-background-50 shadow-lift'
                  : 'bg-background-50 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* comic grid */}
        {list.length > 0 ? (
          <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {list.map((c) => (
              <ComicCard
                key={c.id}
                comic={c}
                followed={followed.includes(c.id)}
                onFollow={toggleFollow}
                onOpen={openComic}
              />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-emotion-unhappy-line text-5xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-4 text-sm text-foreground-500">没搜到相关作品，换个关键词再试试吧～</p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setActive('all');
              }}
              className="mt-4 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600"
            >
              查看全部
            </button>
          </div>
        )}
      </div>

      <ComicModal
        comic={selected}
        followed={selected ? followed.includes(selected.id) : false}
        onFollow={toggleFollow}
        onClose={() => setSelected(null)}
      />

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 animate-pop rounded-full bg-foreground-900 px-5 py-3 text-sm text-background-50 shadow-lift">
          <i className="ri-checkbox-circle-fill mr-2 text-secondary-300" aria-hidden="true"></i>
          {toast}
        </div>
      )}
    </section>
  );
}